// Geometry for the map's "draw an area" filter.
//
// The drawn shape is kept as a plain, serialisable object — never a live
// google.maps overlay — so the same value can sit in component state, travel
// through a saved view's JSON, and be tested against rows without the Maps API
// being loaded. ProjectMap.vue turns one of these back into an overlay when it
// has to redraw; MarketOverview.vue only ever asks "is this row inside?".
//
// Shapes:
//   { kind: "polygon",   path: [{ lat, lng }, ...] }
//   { kind: "rectangle", bounds: { north, south, east, west } }
//   { kind: "circle",    center: { lat, lng }, radius: <metres> }
//
// The maths is local rather than google.maps.geometry.* because the Maps script
// this app loads (index.html) pulls in `places` and `drawing` only — adding the
// geometry library just for two containment checks would cost every page a
// larger Maps payload.

const EARTH_RADIUS_M = 6378137;
const toRad = (deg) => (deg * Math.PI) / 180;

// A row's marker position, read the same way ProjectMap.vue reads it so a pin
// on screen and a row in the table can never disagree about where a project is.
export const rowLatLng = (row) => {
  const g = row && row.geo;
  if (!g) return null;
  const lat = parseFloat(g.lat);
  const lng = parseFloat(g.lng);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
};

// Great-circle distance in metres. Haversine rather than an equirectangular
// approximation: a circle drawn over the Lower Mainland is small enough that
// either would do, but one drawn across a province is not. Exported because the
// circle tool sizes its radius with it, so the shape the user drags out and the
// shape the filter tests are measured the same way.
export const distanceMetres = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Longitude offset from `west`, normalised into 0..360. A rectangle is defined
// as a span running east from its west edge, so this is the space its two edges
// and the test point are compared in — and a box straddling the antimeridian
// still reads as one continuous span instead of two halves 360 degrees apart.
const lngOffset = (lng, west) => {
  let d = lng - west;
  while (d < 0) d += 360;
  while (d >= 360) d -= 360;
  return d;
};

// The same meridian as `lng`, expressed within 180 degrees of `ref`.
//
// Longitude is modular, so two numbers 360 apart name the same place. A ring of
// vertices has to be unwrapped into one continuous run of numbers before any of
// them can be compared by size — otherwise a vertex a little west of the
// reference sits ~360 away from its own neighbour and the ring is torn in half.
const unwrapLng = (lng, ref) => {
  let d = lng - ref;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return ref + d;
};

const inRectangle = (point, bounds) => {
  if (!bounds) return false;
  const { north, south, east, west } = bounds;
  if (point.lat < south || point.lat > north) return false;
  return lngOffset(point.lng, west) <= lngOffset(east, west);
};

const inCircle = (point, center, radius) => {
  if (!center || !(radius > 0)) return false;
  return distanceMetres(center, point) <= radius;
};

// Ray casting, in a longitude space the ring has been unwrapped into.
//
// The unwrapping is what makes this independent of which vertex the user
// clicked first: each vertex is placed within 180 degrees of the one before it,
// so the ring is one continuous run whichever corner it starts at and whichever
// way round it was drawn. The test point is then placed against the middle of
// that run, which is also what carries a ring drawn across the antimeridian.
//
// A vertex lying exactly on the ray is resolved by the half-open `>` / `<=`
// comparison, so a point level with a vertex is counted once, not twice.
const inPolygon = (point, path) => {
  if (!Array.isArray(path) || path.length < 3) return false;

  const xs = [];
  let lo = Infinity;
  let hi = -Infinity;
  let ref = path[0].lng;
  for (let i = 0; i < path.length; i++) {
    ref = unwrapLng(path[i].lng, ref);
    xs.push(ref);
    if (ref < lo) lo = ref;
    if (ref > hi) hi = ref;
  }

  const x = unwrapLng(point.lng, (lo + hi) / 2);
  const y = point.lat;
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const xi = xs[i];
    const yi = path[i].lat;
    const xj = xs[j];
    const yj = path[j].lat;
    if (yi > y !== yj > y) {
      const xCross = xi + ((y - yi) / (yj - yi)) * (xj - xi);
      if (x <= xCross) inside = !inside;
    }
  }
  return inside;
};

// Whether `area` describes a shape that can actually contain anything. A
// half-drawn or restored-but-malformed shape filters nothing rather than
// filtering everything out.
export const isDrawnArea = (area) => {
  if (!area || typeof area !== "object") return false;
  if (area.kind === "polygon")
    return Array.isArray(area.path) && area.path.length >= 3;
  if (area.kind === "rectangle")
    return !!area.bounds && area.bounds.north > area.bounds.south;
  if (area.kind === "circle") return !!area.center && area.radius > 0;
  return false;
};

export const isPointInArea = (point, area) => {
  if (!point || !isDrawnArea(area)) return false;
  if (area.kind === "polygon") return inPolygon(point, area.path);
  if (area.kind === "rectangle") return inRectangle(point, area.bounds);
  return inCircle(point, area.center, area.radius);
};

// The row-level test both filter paths share. A row with no usable coordinates
// is out: it is not on the map, so it cannot be in the area the user drew
// around part of it.
export const isRowInArea = (row, area) => {
  if (!isDrawnArea(area)) return true;
  return isPointInArea(rowLatLng(row), area);
};

// Label for the "area active" chip. Circles report their radius, the other two
// their extent, so the chip says something about the shape rather than just
// naming it.
export const describeArea = (area) => {
  if (!isDrawnArea(area)) return "";
  if (area.kind === "circle") {
    const km = area.radius / 1000;
    return km < 1
      ? `Circle · ${Math.round(area.radius)} m`
      : `Circle · ${km.toFixed(km < 10 ? 1 : 0)} km`;
  }
  if (area.kind === "rectangle") return "Rectangle";
  return `Shape · ${area.path.length} points`;
};
